import { Link } from '@tanstack/react-router';
import type { StaffRow as Staff } from '@mybusinessmate/schema';
import { lastActiveLabel, roleLabel, roleSubtitle, workloadOf } from '../../lib/directory-display';
import { initialsOf } from '../../lib/initials';
import { cx } from '../../lib/cx';
import { RowMenu } from '../directory/row-menu';
import { useToast } from '../ui/toast';
import { staffMenuItems } from './staff-menu';
import { Avatar } from '../ui/avatar';
import { Chip } from '../ui/chip';
import { ProgressBar } from '../ui/progress-bar';
import { Skeleton } from '../ui/skeleton';
import { StatusTag } from '../ui/status-tag';
import { toasts } from '../../lib/toast-presets';

/**
 * The columns of Figma A04.1 "Card · Table": Person 292, Role 190, Skills 200, Workload 200, Last active 130, Status
 * 130, actions 42, with 16px before the first. Below `lg` only the person and the menu remain.
 */
export const STAFF_ROW_GRID =
  'grid items-center pl-(--mbm-space-4) grid-cols-[minmax(0,1fr)_auto] lg:grid-cols-[minmax(0,292fr)_minmax(0,190fr)_minmax(0,200fr)_minmax(0,200fr)_minmax(0,130fr)_minmax(0,130fr)_minmax(0,42fr)]';

const HEAD = 'text-label-small text-text-tertiary uppercase';

export function StaffRowsHeader() {
  return (
    <div className={`${STAFF_ROW_GRID} hidden h-(--mbm-space-8) border-b border-border-line bg-surface-canvas lg:grid`}>
      <span className={HEAD}>Person</span>
      <span className={HEAD}>Role</span>
      <span className={HEAD}>Skills</span>
      <span className={HEAD}>Workload</span>
      <span className={HEAD}>Last active</span>
      <span className={HEAD}>Status</span>
      <span aria-hidden />
    </div>
  );
}

const MAX_SKILLS_SHOWN = 2;

/**
 * A person on the roster (Figma A04.1).
 * TODO(api): an "Invited" status (someone who has not accepted yet, "Invite sent 2 days ago") needs an accepted-invite
 * marker; until then an invited person shows as Active. An account manager's bar needs a max-clients figure.
 */
export function StaffRow({
  person,
  isSelf,
  actions,
}: {
  person: Staff;
  /** Nobody can suspend themselves, so the menu hides it for their own row. */
  isSelf: boolean;
  actions: { onEdit: () => void; onSuspend: () => void; onReinstate: () => void; onView: () => void };
}) {
  const workload = workloadOf(person);
  const suspended = person.status === 'suspended';
  const subtitle = roleSubtitle(person);
  const toast = useToast();
  const menu = staffMenuItems(person, isSelf, {
    ...actions,
    onSoon: (feature) => toast(toasts.comingSoon(`${feature} arrives with a later step.`)),
  });

  return (
    <div
      className={cx(
        STAFF_ROW_GRID,
        'h-(--mbm-space-9) border-b border-border-line transition-colors duration-(--mbm-duration-fast) hover:bg-surface-muted',
      )}
    >
      <Link
        to="/subdomain/admin/staff/$staffId"
        params={{ staffId: person.id }}
        className="flex min-w-0 items-center gap-(--mbm-space-2-5) focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
      >
        <Avatar tone="neutral" initials={initialsOf(person.name)} size="sm" />
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-label-medium text-text-primary">{person.name}</span>
          <span className="truncate text-body-xsmall text-text-tertiary">{person.email}</span>
        </span>
      </Link>

      <span className="hidden min-w-0 flex-col lg:flex">
        <span className="text-body-small text-text-primary">{roleLabel(person.staffRole)}</span>
        {subtitle ? <span className="truncate text-caption text-text-tertiary">{subtitle}</span> : null}
      </span>

      <span className="hidden items-center gap-(--mbm-space-1) lg:flex">
        {person.skills.slice(0, MAX_SKILLS_SHOWN).map((skill) => (
          <Chip key={skill} size="md">
            {skill}
          </Chip>
        ))}
        {person.skills.length > MAX_SKILLS_SHOWN ? (
          <Chip size="md">+{person.skills.length - MAX_SKILLS_SHOWN}</Chip>
        ) : null}
      </span>

      <span className="hidden flex-col gap-(--mbm-space-1-5) pr-(--mbm-space-6) lg:flex">
        <span className={cx('text-caption', workload.full ? 'text-feedback-warning' : 'text-text-secondary')}>
          {workload.text}
        </span>
        {workload.percent !== null ? (
          <ProgressBar
            size="slim"
            value={workload.percent}
            tone={workload.full ? 'amber' : 'dark'}
            label={`${person.name} workload`}
          />
        ) : null}
      </span>

      <span className="hidden text-body-small text-text-primary lg:block">{lastActiveLabel(person.lastActiveAt)}</span>

      <span className="hidden lg:block">
        <StatusTag
          size="md"
          label={suspended ? 'Suspended' : 'Active'}
          tone={suspended ? 'grey' : 'green'}
          variant={suspended ? 'outline' : 'soft'}
        />
      </span>

      <span className="flex justify-end pr-(--mbm-space-3)">
        <RowMenu label={`Actions for ${person.name}`} items={menu} />
      </span>
    </div>
  );
}

export function StaffRowSkeleton() {
  return (
    <div className={`${STAFF_ROW_GRID} h-(--mbm-space-9) border-b border-border-line`}>
      <span className="flex items-center gap-(--mbm-space-2-5)">
        <Skeleton className="size-(--mbm-size-avatar-sm) rounded-full" />
        <Skeleton className="h-(--mbm-space-2-5) w-1/2" />
      </span>
      {Array.from({ length: 5 }, (_, i) => (
        <span key={i} className="hidden lg:block">
          <Skeleton className="h-(--mbm-space-2-5) w-2/3" />
        </span>
      ))}
      <span />
    </div>
  );
}
